// ============================================================================
// api_externalSources.ts
// Acquisition et libération de flux vidéo depuis des URLs externes
// ============================================================================

import { ExternalVideoEntry } from '../../dataTypes/liveStreamManagment/types_webrtc';
import { detectUrlProtocol }   from '../../dataTypes/liveStreamManagment/utils_device';

const externalVideoEls = new Map<string, ExternalVideoEntry>();

export const releaseExternalUrlStream = (url: string): void => {
  const entry = externalVideoEls.get(url);
  if (!entry) return;
  try {
    entry.hls?.destroy();
    entry.dash?.reset();
    entry.video.pause();
    entry.video.srcObject = null;
    entry.video.src = '';
    entry.video.load();
    entry.video.remove();
  } catch { /* best-effort */ }
  externalVideoEls.delete(url);
};

export const acquireExternalUrlStream = async (url: string): Promise<MediaStream> => {
  const protocol = detectUrlProtocol(url);  // ← plus de duplication locale

  if (protocol === 'unsupported') {
    throw new Error(
      `Le protocole ${url.split('://')[0].toUpperCase()} ne peut pas être lu directement dans le navigateur.\n` +
      `Configurez un relais HLS sur votre serveur MediaMTX (path → source: ${url}) puis utilisez l'URL HLS résultante.`
    );
  }

  const existingEntry = externalVideoEls.get(url);
  if (existingEntry && existingEntry.video.readyState >= 2) {
    const stream = (existingEntry.video as any).captureStream?.() ??
                   (existingEntry.video as any).mozCaptureStream?.();
    if (stream) return stream;
  }
  if (existingEntry) releaseExternalUrlStream(url);

  const video = Object.assign(document.createElement('video'), {
    muted: true, autoplay: true, playsInline: true,
    crossOrigin: 'anonymous',
  });
  video.style.display = 'none';
  document.body.appendChild(video);

  const entry: ExternalVideoEntry = { video };
  externalVideoEls.set(url, entry);

  if (protocol === 'hls') {
    let HlsLib = (window as any).Hls;
    if (!HlsLib) {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.13/hls.min.js');
      HlsLib = (window as any).Hls;
    }
    if (HlsLib?.isSupported()) {
      const hls = new HlsLib();
      entry.hls = hls;
      hls.loadSource(url); hls.attachMedia(video);
      await new Promise<void>((resolve, reject) => {
        hls.on(HlsLib.Events.MANIFEST_PARSED, resolve);
        hls.on(HlsLib.Events.ERROR, (_: any, data: any) => { if (data.fatal) reject(new Error(`HLS: ${data.type}`)); });
        setTimeout(() => reject(new Error('HLS timeout (10s)')), 10000);
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
    } else {
      releaseExternalUrlStream(url);
      throw new Error('HLS non supporté sur ce navigateur');
    }
  } else if (protocol === 'dash') {
    let dashjs = (window as any).dashjs;
    if (!dashjs) {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/dashjs/4.7.4/dash.all.min.js');
      dashjs = (window as any).dashjs;
    }
    entry.dash = dashjs.MediaPlayer().create();
    entry.dash.initialize(video, url, true);
  } else {
    video.src = url;
  }

  await video.play();
  await new Promise<void>((resolve, reject) => {
    if (video.readyState >= 2) { resolve(); return; }
    video.oncanplay = () => resolve();
    video.onerror   = () => { releaseExternalUrlStream(url); reject(new Error(`Impossible de lire: ${url}`)); };
    setTimeout(() => { releaseExternalUrlStream(url); reject(new Error('Timeout (15s)')); }, 15000);
  });

  const stream = (video as any).captureStream?.() ?? (video as any).mozCaptureStream?.();
  if (!stream) { releaseExternalUrlStream(url); throw new Error('captureStream() non supporté'); }
  return stream;
};

// ─── Helper chargement dynamique de script ────────────────────────────────────
const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error(`Impossible de charger ${src}`));
    document.head.appendChild(s);
  });